import { useEffect } from 'react';
import { installBundledMarketingTracking } from '../../lib/marketingConversionTracking';

const SITE_URL = 'https://www.ancoraautomations.es';
const ROUTE_SCHEMA_ID = 'ancora-route-schema';

type MarketingHeadProps = {
  title: string;
  description: string;
  path: string;
  imagePath: string;
  imageAlt: string;
  schema: Record<string, unknown>;
};

const canonicalUrl = (path: string) => `${SITE_URL}${path === '/' ? '/' : `${path.replace(/\/$/, '')}/`}`;

export const MarketingHead = ({
  title,
  description,
  path,
  imagePath,
  imageAlt,
  schema,
}: MarketingHeadProps) => {
  useEffect(() => installBundledMarketingTracking(), []);

  useEffect(() => {
    const url = canonicalUrl(path);
    const imageUrl = imagePath.startsWith('http') ? imagePath : `${SITE_URL}${imagePath}`;
    const previousTitle = document.title;
    const previousMetaValues = new Map<string, string>();
    const createdMetaSelectors = new Set<string>();
    const existingCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = existingCanonical?.href;
    let createdCanonical = false;
    const legacyOfferSchemas = [...document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]')]
      .filter((script) => {
        if (script.id === ROUTE_SCHEMA_ID || !script.textContent) return false;
        try {
          const value = JSON.parse(script.textContent) as Record<string, unknown>;
          return Object.prototype.hasOwnProperty.call(value, 'hasOfferCatalog');
        } catch {
          return false;
        }
      })
      .map((script) => ({ script, parent: script.parentNode, nextSibling: script.nextSibling }));

    legacyOfferSchemas.forEach(({ script }) => script.remove());

    const ensureMeta = (
      selector: string,
      attribute: 'name' | 'property',
      key: string,
      content: string,
    ) => {
      let meta = document.querySelector<HTMLMetaElement>(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, key);
        document.head.appendChild(meta);
        createdMetaSelectors.add(selector);
      } else if (!previousMetaValues.has(selector)) {
        previousMetaValues.set(selector, meta.content);
      }
      meta.content = content;
    };

    document.title = title;
    ensureMeta('meta[name="description"]', 'name', 'description', description);
    ensureMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    ensureMeta('meta[property="og:url"]', 'property', 'og:url', url);
    ensureMeta('meta[property="og:title"]', 'property', 'og:title', title);
    ensureMeta('meta[property="og:description"]', 'property', 'og:description', description);
    ensureMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl);
    ensureMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', imageAlt);
    ensureMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    ensureMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
    ensureMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', imageAlt);

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    canonical.href = url;

    const routeSchema = document.createElement('script');
    routeSchema.id = ROUTE_SCHEMA_ID;
    routeSchema.type = 'application/ld+json';
    routeSchema.textContent = JSON.stringify(schema);
    document.head.appendChild(routeSchema);

    return () => {
      document.title = previousTitle;
      routeSchema.remove();
      legacyOfferSchemas.forEach(({ script, parent, nextSibling }) => {
        if (!parent) return;
        if (nextSibling?.parentNode === parent) parent.insertBefore(script, nextSibling);
        else parent.appendChild(script);
      });
      createdMetaSelectors.forEach((selector) => {
        document.querySelector<HTMLMetaElement>(selector)?.remove();
      });
      previousMetaValues.forEach((content, selector) => {
        const meta = document.querySelector<HTMLMetaElement>(selector);
        if (meta) meta.content = content;
      });

      const currentCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (currentCanonical && createdCanonical) {
        currentCanonical.remove();
      } else if (currentCanonical && previousCanonical) {
        currentCanonical.href = previousCanonical;
      }
    };
  }, [description, imageAlt, imagePath, path, schema, title]);

  return null;
};

export const marketingCanonicalUrl = canonicalUrl;
